import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Button, Tooltip, Segmented } from "antd";
import { ExportOutlined, WarningOutlined } from "@ant-design/icons";
import GenericCard from "components/UI/GenericCard/GenericCard";
import AntTable from "components/UI/AntTable";
import DashedTooltip from "components/UI/DashedTooltip";
import getRangeFilterProps from "utils/tableRangeFilter";
import { applyBenjaminiHochberg } from "utils/benjaminiHochberg";
import { returnUpDownClassname } from "utils/helpers";
import { paths } from "utils/constants";

// Calmar with a 1% drawdown floor (a run with no losing trade has 0% drawdown)
const calmarOf = (s) =>
  s ? s.cagr / Math.max(Math.abs(s.maxDrawdown || 0), 1) : null;

// Same checks and thresholds as the single-backtest health check
// (dashboard_backend backtest-health.js), from the batch summary.
export const verdictOf = (row) => {
  const statuses = [];
  statuses.push(row.trades >= 50 ? "pass" : row.trades >= 20 ? "warn" : "fail");

  if (row.holdout) {
    const isCalmar = calmarOf(row.inSample);
    const ratio = isCalmar > 0 ? calmarOf(row.holdout) / isCalmar : null;
    if (row.holdout.trades < 5) statuses.push("warn");
    else if (row.holdout.cagr > 0 && ratio != null && ratio >= 0.5)
      statuses.push("pass");
    else statuses.push(row.holdout.cagr > 0 ? "warn" : "fail");
  }

  if (row.yearsTotal < 2) statuses.push("warn");
  else {
    const share = row.yearsProfitable / row.yearsTotal;
    statuses.push(share >= 0.7 ? "pass" : share >= 0.5 ? "warn" : "fail");
  }

  const calmar = calmarOf(row);
  statuses.push(calmar >= 1 ? "pass" : calmar >= 0.3 ? "warn" : "fail");

  const buyHoldCalmar =
    row.buyHoldCagr / Math.max(Math.abs(row.buyHoldMaxDrawdown || 0), 1);
  statuses.push(
    row.cagr > 0 && calmar >= buyHoldCalmar
      ? "pass"
      : row.cagr > 0
        ? "warn"
        : "fail",
  );

  // signal edge, on the unadjusted p-value like the health check (the
  // table's Edge column shows the multiple-testing-adjusted one)
  if (row.edge) {
    statuses.push(
      row.edge.edgeAbsolute > 0 && row.edge.pValue < 0.05
        ? "pass"
        : row.edge.edgeAbsolute > 0
          ? "warn"
          : "fail",
    );
  }

  if (row.neighborStability == null) statuses.push("warn");
  else
    statuses.push(
      row.neighborStability >= 0.7
        ? "pass"
        : row.neighborStability >= 0.4
          ? "warn"
          : "fail",
    );

  const fails = statuses.filter((s) => s === "fail").length;
  const warns = statuses.filter((s) => s === "warn").length;
  if (fails) return "weak";
  return warns >= 2 ? "caution" : "robust";
};

const VERDICT_STYLE = {
  robust: { label: "Robust", color: "#22c55e", rank: 3 },
  caution: { label: "Caution", color: "#eab308", rank: 2 },
  weak: { label: "Weak", color: "#ef4444", rank: 1 },
};

const fmt = (value, suffix = "", digits = 2) =>
  value == null || !Number.isFinite(+value)
    ? "--"
    : `${(+value).toLocaleString(undefined, { maximumFractionDigits: digits })}${suffix}`;

const pct = (value) => (
  <span className={returnUpDownClassname(value)}>{fmt(value, "%", 1)}</span>
);

const byNumber = (get) => (a, b) => {
  const x = get(a);
  const y = get(b);
  if (x == null && y == null) return 0;
  if (x == null) return -1;
  if (y == null) return 1;
  return x - y;
};

const rowKey = (row) =>
  `${row.symbol}|${row.models.join(",")}|${row.takeProfitPercent}|${row.stopLossPercent}|${row.daysOpenExitCount}`;

// Adds the table's derived fields to a streamed row, in place -- rows arrive
// by the thousand, so no copies
export const annotateRow = (row) => {
  row.key = rowKey(row);
  row.verdict = verdictOf(row);
  // ranked with the 1% drawdown floor, like the verdict and the server's
  // row limit (a near-zero drawdown would make a tiny return rank first)
  row.holdoutCalmar = row.holdout ? +calmarOf(row.holdout).toFixed(2) : null;
  row.yearsShare = row.yearsTotal ? row.yearsProfitable / row.yearsTotal : null;
  return row;
};

// AI Backtests shared-link URL for a row (full backtest + health check)
const backtestUrl = (row, run) => {
  const params = new URLSearchParams({
    shared: "true",
    symbol: row.symbol,
    modelId: row.models.join(", "),
    takeProfitPercent: row.takeProfitPercent,
    stopLossPercent: row.stopLossPercent,
    daysOpenExitCount: row.daysOpenExitCount,
    enterPositionBuyCount: run.base.enterPositionBuyCount,
    exitPositionNoBuyCount: run.base.exitPositionNoBuyCount,
    orderSize: run.base.orderSize,
    orderSizeType: run.base.orderSizeType,
    maxOpenOrders: run.base.maxOpenOrders,
    initialBalance: run.base.initialBalance,
    userCommission: run.base.userCommission,
    resetTrendAfterSell: run.base.resetTrendAfterSell,
    startDate: run.startDate,
    endDate: run.endDate,
  });
  return `${paths.ai}?${params.toString()}`;
};

const ExplorerResults = ({ results, run, summary, modelNames }) => {
  const [filteredInfo, setFilteredInfo] = useState({});
  const [view, setView] = useState("all");

  // rows are already annotated (annotateRow) when they arrive. Default order:
  // verdict first, then holdout Calmar (full Calmar without a holdout) --
  // ranking by the holdout alone floats rows that were only good after the
  // split. Column sorters override it.
  const rows = useMemo(() => {
    // one family: every row of the run was tested together
    const adjusted = applyBenjaminiHochberg(results, {
      groupBy: () => "run",
      getPValue: (row) => row.edge?.pValue,
    });
    const rank = (row) => row.holdoutCalmar ?? calmarOf(row) ?? -Infinity;
    return adjusted.sort(
      (a, b) =>
        VERDICT_STYLE[b.verdict].rank - VERDICT_STYLE[a.verdict].rank ||
        rank(b) - rank(a),
    );
  }, [results]);

  const shown = useMemo(
    () => (view === "robust" ? rows.filter((r) => r.verdict === "robust") : rows),
    [rows, view],
  );

  const hasHoldout = Boolean(run?.splitDate);
  const robustCount = rows.filter((r) => r.verdict === "robust").length;

  // explicit column key so the controlled filteredValue matches what the
  // table reports in onChange
  const range = (key, path) => ({
    key,
    ...getRangeFilterProps(path || [key]),
    filteredValue: filteredInfo[key] || null,
  });

  const columns = [
    {
      title: "",
      key: "open",
      width: 44,
      fixed: "left",
      render: (_, row) => (
        <Tooltip title="Open in AI Backtests with the full health check">
          <Button
            size="small"
            type="text"
            icon={<ExportOutlined />}
            href={backtestUrl(row, run)}
            target="_blank"
          />
        </Tooltip>
      ),
    },
    {
      title: "Verdict",
      key: "verdict",
      dataIndex: "verdict",
      width: 90,
      fixed: "left",
      render: (v) => (
        <span style={{ color: VERDICT_STYLE[v].color, fontWeight: 600 }}>
          {VERDICT_STYLE[v].label}
        </span>
      ),
      sorter: (a, b) => VERDICT_STYLE[a.verdict].rank - VERDICT_STYLE[b.verdict].rank,
      filters: Object.entries(VERDICT_STYLE).map(([value, s]) => ({
        text: s.label,
        value,
      })),
      filteredValue: filteredInfo.verdict || null,
      onFilter: (value, row) => row.verdict === value,
    },
    {
      title: "Symbol",
      dataIndex: "symbol",
      width: 80,
      fixed: "left",
      sorter: (a, b) => a.symbol.localeCompare(b.symbol),
    },
    {
      title: "Models",
      dataIndex: "models",
      width: 200,
      render: (models, row) => (
        <Tooltip title={models.map((id) => modelNames[id] || id).join(" + ")}>
          <span>
            {row.isCorrupted && (
              <WarningOutlined className="yellow me-1" title="Biased model" />
            )}
            {models.length > 1 ? `${models.length}: ` : ""}
            {models.map((id) => modelNames[id] || id.slice(-6)).join(" + ")}
          </span>
        </Tooltip>
      ),
      sorter: (a, b) => a.models.length - b.models.length,
    },
    {
      title: "TP",
      dataIndex: "takeProfitPercent",
      width: 60,
      sorter: byNumber((r) => r.takeProfitPercent),
      ...range("takeProfitPercent"),
    },
    {
      title: "SL",
      dataIndex: "stopLossPercent",
      width: 60,
      render: (v) => (v ? v : "off"),
      sorter: byNumber((r) => r.stopLossPercent),
      ...range("stopLossPercent"),
    },
    {
      title: "Bars",
      dataIndex: "daysOpenExitCount",
      width: 60,
      sorter: byNumber((r) => r.daysOpenExitCount),
      ...range("daysOpenExitCount"),
    },
    {
      title: (
        <DashedTooltip text="Closed trades over the whole range (per year)">
          Trades
        </DashedTooltip>
      ),
      dataIndex: "trades",
      width: 90,
      render: (v, r) => `${v} (${fmt(r.tradesPerYear, "", 0)}/y)`,
      sorter: byNumber((r) => r.trades),
      ...range("trades"),
    },
    {
      title: "CAGR",
      dataIndex: "cagr",
      width: 80,
      render: pct,
      sorter: byNumber((r) => r.cagr),
      ...range("cagr"),
    },
    {
      title: (
        <DashedTooltip text="Closed-trade max drawdown">Max DD</DashedTooltip>
      ),
      dataIndex: "maxDrawdown",
      width: 80,
      render: (v) => <span className="red">{fmt(v, "%", 1)}</span>,
      sorter: byNumber((r) => r.maxDrawdown),
      ...range("maxDrawdown"),
    },
    {
      title: "Calmar",
      dataIndex: "calmarRatio",
      width: 75,
      render: (v) => fmt(v),
      sorter: byNumber((r) => r.calmarRatio),
      ...range("calmarRatio"),
    },
    {
      title: "Sharpe",
      dataIndex: "sharpeRatio",
      width: 70,
      render: (v) => fmt(v),
      sorter: byNumber((r) => r.sharpeRatio),
      ...range("sharpeRatio"),
    },
    {
      title: "Win %",
      dataIndex: "successRate",
      width: 70,
      render: (v) => fmt(v, "%", 1),
      sorter: byNumber((r) => r.successRate),
      ...range("successRate"),
    },
    {
      title: "PF",
      dataIndex: "profitFactor",
      width: 65,
      render: (v) => (v == null ? "no loss" : fmt(v)),
      sorter: byNumber((r) => r.profitFactor),
      ...range("profitFactor"),
    },
    {
      title: (
        <DashedTooltip text="Average net % per trade">Expect.</DashedTooltip>
      ),
      dataIndex: "expectancy",
      width: 75,
      render: pct,
      sorter: byNumber((r) => r.expectancy),
      ...range("expectancy"),
    },
    ...(hasHoldout
      ? [
          {
            title: (
              <DashedTooltip text={`From ${run.splitDate} on, measured separately. The number to trust: it wasn't used to pick the row.`}>
                Holdout CAGR
              </DashedTooltip>
            ),
            dataIndex: ["holdout", "cagr"],
            width: 95,
            render: pct,
            sorter: byNumber((r) => r.holdout?.cagr),
            ...range("holdoutCagr", ["holdout", "cagr"]),
          },
          {
            title: "Holdout DD",
            dataIndex: ["holdout", "maxDrawdown"],
            width: 90,
            render: (v) => <span className="red">{fmt(v, "%", 1)}</span>,
            sorter: byNumber((r) => r.holdout?.maxDrawdown),
            ...range("holdoutDd", ["holdout", "maxDrawdown"]),
          },
          {
            title: (
              <DashedTooltip text="Holdout CAGR / holdout max drawdown (drawdown floored at 1%). In brackets: the same before the split.">
                Holdout Calmar
              </DashedTooltip>
            ),
            dataIndex: "holdoutCalmar",
            width: 95,
            render: (v, r) =>
              `${fmt(v)} (${fmt(r.inSample && calmarOf(r.inSample))})`,
            sorter: byNumber((r) => r.holdoutCalmar),
            ...range("holdoutCalmar"),
          },
          {
            title: "Holdout trades",
            dataIndex: ["holdout", "trades"],
            width: 80,
            sorter: byNumber((r) => r.holdout?.trades),
            ...range("holdoutTrades", ["holdout", "trades"]),
          },
        ]
      : []),
    {
      title: (
        <DashedTooltip text="Full calendar years with a positive return / all full years">
          Years +
        </DashedTooltip>
      ),
      dataIndex: "yearsShare",
      width: 75,
      render: (v, r) =>
        r.yearsTotal ? `${r.yearsProfitable}/${r.yearsTotal}` : "--",
      sorter: byNumber((r) => r.yearsShare),
      ...range("yearsShare"),
    },
    {
      title: (
        <DashedTooltip text="Median Calmar of the next/previous grid value on each axis, relative to this row. Near 1 = stable plateau; near 0 = lucky spike.">
          Stability
        </DashedTooltip>
      ),
      dataIndex: "neighborStability",
      width: 85,
      render: (v, r) => (
        <Tooltip
          title={`${r.neighborsProfitable ?? 0}/${r.neighbors} neighbors profitable`}
        >
          <span
            className={
              v == null ? "" : v >= 0.7 ? "green" : v >= 0.4 ? "yellow" : "red"
            }
          >
            {fmt(v)}
          </span>
        </Tooltip>
      ),
      sorter: byNumber((r) => r.neighborStability),
      ...range("neighborStability"),
    },
    {
      title: (
        <DashedTooltip text="Signal hits the take profit more often than no-signal days: lift and p-value adjusted for the number of rows tested (Benjamini-Hochberg).">
          Edge
        </DashedTooltip>
      ),
      dataIndex: "pValueAdjusted",
      width: 110,
      render: (v, r) =>
        r.edge ? (
          <span className={v != null && v < 0.05 ? "green" : ""}>
            {fmt(r.edge.edgeLift)}x (p {fmt(v, "", 3)})
          </span>
        ) : (
          "--"
        ),
      sorter: byNumber((r) => r.pValueAdjusted),
      ...range("pValueAdjusted"),
    },
    {
      title: "B&H CAGR",
      dataIndex: "buyHoldCagr",
      width: 85,
      render: pct,
      sorter: byNumber((r) => r.buyHoldCagr),
    },
  ];

  return (
    <GenericCard title="Results" className="table-card" maxHeight={2000}>
      {summary && (
        <div className="text-left mb-3 flex flex-col gap-1">
          <span>
            Tested <b>{summary.tested.toLocaleString()}</b> backtests
            {summary.durationMs
              ? ` in ${(summary.durationMs / 1000).toFixed(1)}s`
              : ""}
            ; showing {rows.length.toLocaleString()} with enough trades,{" "}
            <span className="green">{robustCount.toLocaleString()} robust</span>.
            {summary.truncated > 0 &&
              ` Over the row limit: showing the best ${rows.length.toLocaleString()} by ${
                hasHoldout ? "holdout " : ""
              }Calmar, ${summary.truncated.toLocaleString()} weaker rows dropped.`}
            {summary.cancelled && " Run was stopped early."}
          </span>
          <span style={{ opacity: 0.7 }}>
            Picking the best of many backtests mostly picks luck. Sort by the
            holdout, prefer Robust rows with high stability, and open a row to
            run its full health check before trading it.
          </span>
        </div>
      )}
      <div className="flex gap-3 items-center mb-3 flex-wrap">
        <Segmented
          value={view}
          onChange={setView}
          options={[
            { label: `All (${rows.length.toLocaleString()})`, value: "all" },
            { label: `Robust (${robustCount.toLocaleString()})`, value: "robust" },
          ]}
        />
        <Button onClick={() => setFilteredInfo({})}>Reset filters</Button>
      </div>
      <AntTable
        rowKey="key"
        dataSource={shown}
        columns={columns}
        size="small"
        scroll={{ x: 2100, y: 640 }}
        onChange={(pagination, filters) => setFilteredInfo(filters)}
      />
    </GenericCard>
  );
};

ExplorerResults.propTypes = {
  results: PropTypes.array,
  run: PropTypes.object,
  summary: PropTypes.object,
  modelNames: PropTypes.object,
};

export default ExplorerResults;
